import { type Provider, createContext, useContext, Consumer } from 'react'

const NullSymbol = Symbol('Null')

export type CreateContextReturn<T> = [Provider<T>, () => T, Consumer<T>]

export function createSafeContext<T>(
  displayName?: string,
): CreateContextReturn<T> {
  const Context = createContext<T | typeof NullSymbol>(NullSymbol)
  Context.displayName = displayName ?? 'SafeContext'

  function useSafeContext() {
    const context = useContext(Context)

    if (context === NullSymbol) {
      const error = new Error(`[${Context.displayName}]: Provider not found.`)
      error.name = '[Error] Context'

      throw error
    }

    return context
  }

  return [
    Context.Provider as Provider<T>,
    useSafeContext,
    Context.Consumer as Consumer<T>,
  ]
}
